import * as React from 'react'
import { cn } from 'lib/utils'
import { ZoomIn, ZoomOut, X } from 'lucide-react'

const ZOOM_STEP = 0.25
const MIN_ZOOM = 0.5
const MAX_ZOOM = 4

export interface ImageLightboxProps {
	src: string
	alt?: string
	open: boolean
	onClose: () => void
}

const ImageLightbox = ({ src, alt = '', open, onClose }: ImageLightboxProps) => {
	const [zoom, setZoom] = React.useState(1)
	const [position, setPosition] = React.useState({ x: 0, y: 0 })
	const [isDragging, setIsDragging] = React.useState(false)
	const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 })

	// Reset zoom and position when image changes or lightbox opens
	React.useEffect(() => {
		if (open) {
			setZoom(1)
			setPosition({ x: 0, y: 0 })
		}
	}, [open, src])

	// Close on Escape key
	React.useEffect(() => {
		if (!open) return
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose()
		}
		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [open, onClose])

	// Prevent body scroll when lightbox is open
	React.useEffect(() => {
		if (open) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = ''
		}
		return () => {
			document.body.style.overflow = ''
		}
	}, [open])

	if (!open) return null

	const zoomIn = () => setZoom(z => Math.min(z + ZOOM_STEP, MAX_ZOOM))
	const zoomOut = () => {
		setZoom(z => {
			const next = Math.max(z - ZOOM_STEP, MIN_ZOOM)
			if (next <= 1) setPosition({ x: 0, y: 0 })
			return next
		})
	}
	const resetZoom = () => {
		setZoom(1)
		setPosition({ x: 0, y: 0 })
	}

	const handleBackdropClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) onClose()
	}

	const handleMouseDown = (e: React.MouseEvent) => {
		if (zoom <= 1) return
		e.preventDefault()
		setIsDragging(true)
		setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
	}

	const handleMouseMove = (e: React.MouseEvent) => {
		if (!isDragging) return
		setPosition({
			x: e.clientX - dragStart.x,
			y: e.clientY - dragStart.y,
		})
	}

	const handleMouseUp = () => setIsDragging(false)

	const handleWheel = (e: React.WheelEvent) => {
		e.stopPropagation()
		if (e.deltaY < 0) zoomIn()
		else zoomOut()
	}

	const zoomPercent = Math.round(zoom * 100)

	return (
		<div
			className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/80"
			onClick={handleBackdropClick}
			onMouseMove={handleMouseMove}
			onMouseUp={handleMouseUp}
			onMouseLeave={handleMouseUp}
		>
			{/* Toolbar */}
			<div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 rounded-full px-4 py-2 z-10">
				<button
					type="button"
					onClick={zoomOut}
					disabled={zoom <= MIN_ZOOM}
					className="p-1.5 text-white hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
					title="Zoom out"
				>
					<ZoomOut className="w-5 h-5" />
				</button>
				<button
					type="button"
					onClick={resetZoom}
					className="text-white text-sm font-medium min-w-[48px] text-center hover:text-gray-300 transition-colors"
					title="Reset zoom"
				>
					{zoomPercent}%
				</button>
				<button
					type="button"
					onClick={zoomIn}
					disabled={zoom >= MAX_ZOOM}
					className="p-1.5 text-white hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
					title="Zoom in"
				>
					<ZoomIn className="w-5 h-5" />
				</button>
				<div className="w-px h-5 bg-white/30 mx-1" />
				<button
					type="button"
					onClick={onClose}
					className="p-1.5 text-white hover:text-gray-300 transition-colors"
					title="Close"
				>
					<X className="w-5 h-5" />
				</button>
			</div>

			{/* Image */}
			<img
				src={src}
				alt={alt}
				className={cn(
					'max-h-[85vh] max-w-[90vw] object-contain select-none transition-transform duration-150',
					zoom > 1 ? 'cursor-grab' : 'cursor-zoom-in',
					isDragging && 'cursor-grabbing !duration-0',
				)}
				style={{
					transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
				}}
				onClick={(e) => {
					e.stopPropagation()
					if (zoom <= 1) zoomIn()
				}}
				onMouseDown={handleMouseDown}
				onWheel={handleWheel}
				draggable={false}
			/>
		</div>
	)
}

export { ImageLightbox }
